/**
 * Static Atom Rendering from Recipe
 *
 * Renders atoms using recipes with separated hitbox and visual layers.
 * Follows the same pattern as renderBondFromRecipe for consistency.
 */

import type { JSX } from 'react';
import type { FormatOptions } from '@alchemiesolns/svg-format';
import { getAtomColor } from '@alchemiesolns/svg-format';
import { getFormat } from './utils/formatContext';
import { getOffsetX, calculateGroupOffset } from '@alchemiesolns/line-angle-util';
import { estimateCharWidth } from './atomLabelBounds';

/**
 * Atom recipe containing pre-calculated rendering data
 */
export interface AtomRecipe {
  /** Atom identifier (e.g., "atom-1") */
  atomId: string;
  /** Atom index in container */
  atomIndex: number;
  /** Chemical element symbol */
  element: string;
  /** Display symbol (may include implicit hydrogens, e.g., "CH3") */
  displaySymbol: string;
  /** Atom type: "text" for labeled atoms, "carbon" for paper carbons */
  type: 'text' | 'carbon';
  /** Visibility state */
  visibility: 'visible' | 'hidden';
  /** Bond direction for group alignment (optional) */
  bondDirection?: 'left' | 'right' | 'default';
  /** Whether this is a group (multi-character label like TBDMS, SnBu3) */
  isGroup?: boolean;
}

export interface AtomVisualStyle {
  fill?: string;
  fontSize?: number;
  opacity?: number;
}

/**
 * Calculate dynamic hitbox radius based on atom display symbol length
 *
 * Single-character atoms (C, N, O, etc.) use the base radius of 3.0 SVG units.
 * Multi-character atoms (Cl, Br, Na, etc.) get larger hitboxes proportional to text width.
 *
 * @param displaySymbol - The atom's display text (e.g., "C", "Cl", "CH3")
 * @param fontSize - Font size in SVG units (typically ~2.75)
 * @returns Hitbox radius in SVG units
 *
 * @example
 * ```typescript
 * // Single-character atom
 * calculateHitboxRadius("C", 2.75) // 3.0
 *
 * // Multi-character atom
 * calculateHitboxRadius("Cl", 2.75) // ~4.3
 * calculateHitboxRadius("CH3", 2.75) // ~5.8
 * ```
 */
export function calculateHitboxRadius(displaySymbol: string, fontSize: number, isSmallScale: boolean = false): number {
  const format = getFormat();
  const baseRadius = format.atom.radius;

  // For single-character atoms or small-scale atoms, use the base radius
  if (displaySymbol.length <= 1 || isSmallScale) {
    return baseRadius;
  }

  // For multi-character atoms, estimate text width by summing individual character widths
  let estimatedTextWidth = 0;
  for (let i = 0; i < displaySymbol.length; i++) {
    estimatedTextWidth += estimateCharWidth(displaySymbol[i], fontSize);
  }

  // Hitbox radius is half the text width plus padding for comfortable clicking
  const padding = 0.2;
  const dynamicRadius = estimatedTextWidth / 2 + padding;

  // Return the larger of base radius or calculated radius
  return Math.max(baseRadius, dynamicRadius);
}

/**
 * Renders an atom from a recipe and static position
 *
 * Creates two layers:
 * 1. Invisible hitbox (circle) for interaction - uses 3.0 radius
 * 2. Visual element (text or paper carbon circle)
 *
 * @param recipe - Precalculated atom recipe with symbol and metadata
 * @param atomX - X position of atom (SVG coordinates)
 * @param atomY - Y position of atom (SVG coordinates)
 * @param format - Format object from useFormat() hook
 * @param visualStyle - Optional style overrides for the visible atom (selection/hover effects)
 */
export function renderAtomFromRecipe(
  recipe: AtomRecipe,
  atomX: number,
  atomY: number,
  format: FormatOptions,
  visualStyle?: AtomVisualStyle
): JSX.Element {
  const color = visualStyle?.fill || getAtomColor(recipe.element);
  const fontSize = visualStyle?.fontSize || format.fontSize;
  const opacity = visualStyle?.opacity;

  // Calculate offset for centering non-hydrogen part or aligning groups
  // Only apply to text atoms, not paper carbons (which are small circles)
  let hydrogenOffset = 0;
  if (recipe.type === 'text') {
    if (recipe.isGroup && recipe.bondDirection) {
      // Group offset for multi-character labels
      hydrogenOffset = calculateGroupOffset(recipe.displaySymbol, recipe.bondDirection, fontSize);
    } else {
      // Regular hydrogen offset for non-group atoms
      hydrogenOffset = getOffsetX(recipe.displaySymbol, fontSize);
    }
  }

  if (recipe.type === 'carbon') {
    // Paper carbon - render as circle (keep circular for consistency with visual)
    // Use baseRadius for small paper carbons instead of calculating from display symbol
    const baseRadius = format.atom.radius + 0.4;

    return (
      <g
        key={`${recipe.atomId}-group`}
        transform={`translate(${atomX}, ${atomY})`}
      >
        {/* Invisible hitbox circle - uses baseRadius for paper carbons */}
        <circle
          key={`${recipe.atomId}-hitbox`}
          id={recipe.atomId}
          className="svg-atom svg-atom-hitbox"
          data-atom-id={recipe.atomId}
          data-atom-index={String(recipe.atomIndex)}
          data-atom-role="hitbox"
          data-element={recipe.element}
          data-x={String(atomX)}
          data-y={String(atomY)}
          data-visibility={recipe.visibility}
          cx={0}
          cy={0}
          r={baseRadius}
          fill="transparent"
          stroke="none"
          style={{ pointerEvents: 'fill' }}
        />

        {/* Visual paper carbon circle */}
        <circle
          key={`${recipe.atomId}-visual`}
          className="svg-atom-visual"
          data-atom-role="visual"
          cx={0}
          cy={0}
          r={format.atom.radius * format.atom.carbon.scale}
          fill={color}
          opacity={opacity ?? 1}
          style={{ pointerEvents: 'none' }}
        />
      </g>
    );
  } else {
    // Text atom - render as text element with rectangular hitbox
    return (
      <g
        key={`${recipe.atomId}-group`}
        transform={`translate(${atomX}, ${atomY})`}
      >
        {/* Invisible hitbox rectangle - dynamically sized for text atoms */}
        {/* <rect
          key={`${recipe.atomId}-hitbox`}
          id={recipe.atomId}
          className="svg-atom svg-atom-hitbox"
          data-atom-id={recipe.atomId}
          data-atom-index={String(recipe.atomIndex)}
          data-atom-role="hitbox"
          data-element={recipe.element}
          data-x={String(atomX)}
          data-y={String(atomY)}
          data-visibility={recipe.visibility}
          x={-hitboxWidth / 2}
          y={-hitboxHeight / 2}
          width={hitboxWidth}
          height={hitboxHeight}
          fill="red"
          stroke="none"
          style={{ pointerEvents: 'fill' }}
        /> */}

        {/* Visual text element */}
        <text
          key={`${recipe.atomId}-visual`}
          className="svg-atom svg-text svg-atom-visual"
          data-atom-role="visual"
          x={format.atom.symbolOffset[0] + hydrogenOffset}
          y={format.atom.symbolOffset[1]}
          fontSize={fontSize}
          fontFamily={format.fontFamily}
          fill={color}
          textAnchor="middle"
          dominantBaseline="central"
          opacity={opacity}
          style={{ pointerEvents: 'none' }}
        >
          {recipe.displaySymbol}
        </text>
      </g>
    );
  }
}
